/**
 * Shape-type predicates.
 *
 * The PowerPoint host returns `Shape.type` as the ENUM VALUE, which is
 * PascalCase ("GeometricShape", "Group", "Table", "Placeholder"), not the
 * camelCase enum KEY. Comparing against the key silently matches nothing, so
 * every read path that gated on `type` went blind: text was never loaded,
 * groups were never expanded, tables were never read, and a fully populated
 * deck reported itself empty.
 *
 * Everything here normalizes before comparing, so either casing works.
 */

/** Canonical lowercase form of a shape type, tolerant of host casing. */
export function normType(t: unknown): string {
  return String(t ?? "").toLowerCase();
}

/** Shape types that can never carry a text frame. Anything else we try to read. */
const NON_TEXT = new Set([
  "group",
  "image",
  "line",
  "table",
  "chart",
  "media",
  "ole",
  "model3d",
  "ink",
  "graphic",
  "diagram",
  "smartart",
  "contentapp",
]);

/**
 * Shapes whose text we ATTEMPT to read. Deliberately permissive: "unsupported"
 * is included because some hosts report branded-template placeholders that way,
 * and a shape that turns out to have no text frame is absorbed by the
 * fault-isolated loader. A false positive costs one sync; a false negative
 * makes real content invisible.
 */
export function canHoldText(t: unknown): boolean {
  return !NON_TEXT.has(normType(t));
}

export function isGroupType(t: unknown): boolean {
  return normType(t) === "group";
}

export function isTableType(t: unknown): boolean {
  return normType(t) === "table";
}

export function isPlaceholderType(t: unknown): boolean {
  return normType(t) === "placeholder";
}
